
import React from 'react';
import { 
  Activity, 
  ShieldAlert, 
  Navigation, 
  Layers, 
  Camera, 
  Wind, 
  ChevronRight, 
  // Added missing ArrowRight import
  ArrowRight,
  Monitor, 
  Cpu, 
  Signal, 
  Plane, 
  Radar, 
  Target, 
  Users, 
  Zap, 
  Fingerprint,
  Scan,
  Car,
  Lightbulb,
  Vote,
  Smartphone,
  Globe,
  Workflow,
  TrendingUp,
  Link2,
  Lock,
  BarChart3, 
  Network,
  TrafficCone,
  AlertTriangle,
  Siren,
  Eye,
  History
} from 'lucide-react';

const SmartCityPage: React.FC = () => {
  return (
    <div className="pt-48 pb-20 bg-[#0a0a0a]">
      {/* 1. HERO - THE CITY BRAIN */}
      <section className="px-6 max-w-7xl mx-auto mb-40 relative">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-blue/10 blur-[150px] rounded-full pointer-events-none"></div>
        <div className="max-w-5xl space-y-12 relative z-10 parallax-layer" data-speed="0.1">
          <div className="inline-flex items-center space-x-3 px-6 py-3 glass-island border-brand-blue/30 rounded-full animate-pulse">
            <span className="text-[10px] uppercase tracking-[0.4em] text-brand-blue font-black">The Nervous System</span>
          </div>
          <h2 className="text-6xl md:text-[8rem] font-serif italic tracking-tighter leading-[0.8] text-white">
            The City Brain<span className="text-brand-blue animate-pulse">.</span>
          </h2>
          <p className="text-3xl text-white font-bold leading-relaxed max-w-4xl opacity-80">
            Smart city technologies succeed only when communication, coordination, and public input are unified. 
            Boulai is building a central, living intelligence layer that connects people, infrastructure, and decision-makers in real time.
          </p>
          <p className="text-xl text-white/40 max-w-2xl font-medium">
            This is not a collection of disconnected tools. It is a single system that senses, learns, and improves how cities operate.
          </p>
        </div>
      </section>

      {/* 2. CENTRAL INTELLIGENCE LAYER */}
      <section className="py-40 px-6 bg-[#0c0c0d] border-y border-white/5 relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-24 items-center">
          <div className="space-y-12">
            <h3 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black">UNIFIED FABRIC</h3>
            <h4 className="text-4xl md:text-5xl font-serif italic leading-[1.1] text-white tracking-tighter">
              The Central Intelligence Layer.
            </h4>
            <p className="text-xl text-white font-bold leading-relaxed opacity-60">
              All signals flow into one unified fabric, powering real-time awareness and coordinated action across the city.
            </p>
            <div className="grid grid-cols-1 gap-4">
              {[
                "Citizen engagement and participation",
                "City infrastructure and sensors",
                "Mobility and transportation systems",
                "Environmental and situational inputs",
                "AI models and decision engines"
              ].map((item, i) => (
                <div key={i} className="flex items-center space-x-4 p-4 glass-island rounded-xl border-white/5 group hover:border-brand-blue/30 transition-all">
                  <div className="w-2 h-2 rounded-full bg-brand-blue group-hover:scale-150 transition-transform"></div>
                  <span className="text-sm font-bold text-white/70">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-lg text-brand-blue font-black italic">
              Boulai does not replace existing systems. It connects them — and makes them smarter together.
            </p>
          </div>

          <div className="relative parallax-layer" data-speed="-0.05">
            <div className="glass-island p-16 rounded-[4rem] border-white/10 relative overflow-hidden group shadow-3xl aspect-square flex flex-col items-center justify-center bg-black/50">
               <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/20 to-transparent pointer-events-none"></div>
               <div className="relative z-10 flex items-center justify-center w-full h-full">
                  <div className="absolute w-[95%] h-[95%] border border-brand-blue/10 rounded-full animate-spin-slow"></div>
                  <div className="absolute w-[75%] h-[75%] border border-white/5 rounded-full animate-reverse-spin"></div>
                  
                  {/* Floating Icons */}
                  <div className="absolute w-full h-full pointer-events-none">
                    <Car className="absolute top-10 left-10 text-brand-blue/40 animate-float" size={32} />
                    <TrafficCone className="absolute top-1/4 right-8 text-brand-blue/60" size={24} />
                    <Siren className="absolute bottom-1/4 left-8 text-red-500/40 animate-pulse" size={32} />
                    <Plane className="absolute top-10 right-1/4 text-brand-blue/40 animate-float" size={32} />
                    <Fingerprint className="absolute bottom-10 right-1/4 text-brand-blue/70 animate-pulse" size={32} />
                    <Vote className="absolute bottom-1/3 right-10 text-brand-blue/50" size={28} />
                    <Smartphone className="absolute top-1/2 left-4 text-brand-blue/60 -translate-y-1/2 animate-float" size={24} />
                  </div>

                  <div className="relative z-20 flex flex-col items-center gap-6">
                     <Cpu className="text-white animate-pulse" size={64} />
                     <div className="flex gap-2">
                        <div className="w-2 h-2 rounded-full bg-brand-blue animate-bounce"></div>
                        <div className="w-2 h-2 rounded-full bg-brand-blue animate-bounce [animation-delay:-0.1s]"></div>
                        <div className="w-2 h-2 rounded-full bg-brand-blue animate-bounce [animation-delay:-0.2s]"></div>
                     </div>
                  </div>
               </div>
               <div className="absolute bottom-16 text-center z-10 w-full">
                  <h4 className="text-2xl font-black uppercase tracking-tighter text-white">Neural City Fabric</h4>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-4 italic">Processing Node: Active</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. THE FABRIC DETAILS */}
      <section className="py-40 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { 
              icon: <Camera size={32}/>, 
              title: "Visual Ingest", 
              desc: "Detect crashes, congestion, and blockages instantly across city camera networks." 
            },
            { 
              icon: <Wind size={32}/>, 
              title: "Environment Tracking", 
              desc: "Detect air spikes, wildfires, flooding, and hazards through deep integration." 
            },
            { 
              icon: <Fingerprint size={32}/>, 
              title: "Citizen Signals", 
              desc: "Ingest verified citizen input, turning residents into active participants in city operations." 
            }
          ].map((item, i) => (
            <div key={i} className="p-12 glass-island rounded-[3rem] border-white/10 bg-white/[0.01] hover:border-brand-blue/30 transition-all duration-700">
               <div className="w-20 h-20 rounded-2xl bg-brand-blue/10 flex items-center justify-center text-brand-blue mb-8">
                 {item.icon}
               </div>
               <h4 className="text-2xl font-black uppercase tracking-tight text-white mb-6">{item.title}</h4>
               <p className="text-lg text-white font-medium opacity-50 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4. DECISION COCKPIT */}
      <section className="py-40 px-6 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
             <div className="order-2 lg:order-1 relative">
                <div className="glass-island p-16 rounded-[4rem] border-white/10 relative overflow-hidden group shadow-3xl">
                   <Monitor size={400} className="absolute -right-20 -bottom-20 text-white/[0.02] rotate-12" />
                   <div className="relative z-10 grid grid-cols-2 gap-8">
                      {[
                        { icon: <History />, label: "Run Simulations" },
                        { icon: <AlertTriangle />, label: "Predict Secondary Risks" },
                        { icon: <Navigation />, label: "Estimate Resources" },
                        { icon: <Layers />, label: "Plan Safer Layouts" }
                      ].map((card, i) => (
                        <div key={i} className="p-8 bg-black/40 rounded-3xl border border-white/5 hover:border-brand-blue transition-all flex flex-col items-center text-center gap-4">
                           <div className="text-brand-blue">{card.icon}</div>
                           <span className="text-[10px] uppercase tracking-widest font-black text-white/60">{card.label}</span>
                        </div>
                      ))}
                   </div>
                </div>
             </div>
             <div className="order-1 lg:order-2 space-y-12">
                <h3 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black">DIGITAL TWIN</h3>
                <h4 className="text-4xl md:text-6xl font-serif italic text-white leading-tight">The Decision Cockpit.</h4>
                <p className="text-xl text-white font-bold leading-relaxed opacity-60">
                  The City Brain powers a real-time Decision Cockpit — a living digital twin of the urban environment. Operator can model impacts before deployment and redesign intersections based on safety outcomes.
                </p>
                <div className="p-8 border-l-2 border-brand-blue bg-brand-blue/5">
                   <p className="text-lg text-white font-bold italic">The twin evolves continuously as new data is ingested — becoming more predictive over time.</p>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* 5. EMERGENCY RESPONSE AS NATIVE */}
      <section className="py-40 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-24 items-center">
          <div className="space-y-12">
            <h3 className="text-xs uppercase tracking-[0.5em] text-red-500 font-black">NATIVE CAPABILITY</h3>
            <h4 className="text-4xl md:text-5xl font-serif italic text-white leading-tight">Emergency Response as a Native Capability.</h4>
            <p className="text-xl text-white font-bold leading-relaxed opacity-60">
              ERS are not bolt-ons. They are native to the City Brain. When an incident occurs, situational awareness is shared across Fire, EMS, and Police instantly.
            </p>
            <p className="text-lg text-white/40 font-medium">
              Instead of fragmented communication, Boulai creates one shared operational picture — pushing verified, high-quality intelligence to all parties simultaneously.
            </p>
          </div>
          <div className="relative flex justify-center">
             <div className="w-80 h-80 relative">
                <div className="absolute inset-0 bg-red-500/10 blur-3xl animate-pulse rounded-full"></div>
                <div className="relative z-10 glass-island rounded-full w-full h-full flex items-center justify-center border-red-500/20">
                   <Siren size={80} className="text-red-500 animate-bounce" />
                </div>
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-2xl animate-float">
                   <AlertTriangle className="text-red-500" size={24} />
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* 6. FROM REACTIVE TO PREDICTIVE */}
      <section className="py-40 px-6 bg-[#0d0d0d] border-y border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center">
           <h2 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black mb-12">PREDICTION CYCLE</h2>
           <h3 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter mb-16">
             Reactive <ArrowRight className="inline mx-4" /> Preventive <ArrowRight className="inline mx-4" /> Predictive
           </h3>
           <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
              {[
                { title: "Risk Intersections", val: "Predict high-accident zones." },
                { title: "Crime Patterns", val: "Identify potential spikes early." },
                { title: "Infrastructure Stress", val: "Detect points of failure." },
                { title: "Crowd Behavior", val: "Manage anomalies during events." }
              ].map((box, i) => (
                <div key={i} className="p-8 glass-island rounded-3xl border-white/5 hover:border-brand-blue/30 transition-all">
                   <h5 className="font-black text-sm uppercase text-white mb-4 tracking-widest">{box.title}</h5>
                   <p className="text-white/40 font-bold">{box.val}</p>
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* 7. A LIVING, LEARNING SYSTEM */}
      <section className="py-40 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-24 items-center">
           <div className="space-y-12">
              <h3 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black">CONTINUOUS LEARNING</h3>
              <h4 className="text-4xl md:text-5xl font-serif italic text-white leading-tight">Boulai doesn't just support cities. It learns them.</h4>
              <p className="text-xl text-white font-bold opacity-60 leading-relaxed">
                Every interaction trains the system. Citizen engagement improves sentiment baselines, and operational decisions feed back into future planning models.
              </p>
              <div className="grid gap-6">
                 {[
                   { icon: <TrendingUp />, text: "Compounding Advantage" },
                   { icon: <BrainCircuit size={24} />, text: "Institutional Intelligence" },
                   { icon: <CheckCircle2 size={24} />, text: "Higher Public Trust" }
                 ].map((adv, i) => (
                   <div key={i} className="flex items-center space-x-4 p-6 glass-island rounded-2xl border-white/10 group hover:border-brand-blue/30 transition-all">
                      <div className="text-brand-blue group-hover:scale-110 transition-transform">{adv.icon}</div>
                      <span className="text-xl font-bold text-white">{adv.text}</span>
                   </div>
                 ))}
              </div>
           </div>
           <div className="relative">
              <div className="glass-island p-16 rounded-[4rem] text-center space-y-12 relative z-10 border-white/10 shadow-3xl animate-float">
                 <div className="w-24 h-24 bg-brand-blue/10 rounded-full flex items-center justify-center text-brand-blue mx-auto">
                    <History size={48} />
                 </div>
                 <h4 className="text-3xl font-serif italic text-white tracking-tighter leading-tight">Data that grows in value <br/> every year.</h4>
                 <p className="text-white text-lg font-medium opacity-60">The more you use it, the sharper the intelligence becomes.</p>
              </div>
           </div>
        </div>
      </section>

      {/* 8. WHAT UNLOCKS OVER TIME */}
      <section className="py-40 px-6 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black mb-12">ROADMAP EVOLUTION</h2>
          <h3 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter mb-12">Capabilities unlock naturally.</h3>
          <div className="grid md:grid-cols-3 gap-12 text-left">
            {[
              { title: "As Engagement Grows", desc: "Deeper public insight, accurate tracking, clearer policy impact measurement." },
              { title: "As ERS Data Grows", desc: "Predictive safety models, optimized response strategies, reduced system load." },
              { title: "As Infra Grows", desc: "Smarter mobility orchestration and preventive infrastructure planning." }
            ].map((un, i) => (
              <div key={i} className="p-12 glass-island rounded-[3rem] border-white/10 hover:border-brand-blue transition-all">
                 <h4 className="text-xl font-black uppercase text-brand-blue mb-6 tracking-tight">{un.title}</h4>
                 <p className="text-lg text-white/50 font-bold leading-relaxed">{un.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. WHY EARLY ADOPTERS WIN */}
      <section className="py-40 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-24 items-center">
          <div className="space-y-12">
             <h3 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black">STRATEGIC ADVANTAGE</h3>
             <h4 className="text-4xl md:text-5xl font-serif italic text-white leading-tight">Why Early Adopters Win.</h4>
             <p className="text-xl text-white font-bold opacity-60 leading-relaxed">
               Cities and governments that adopt early benefit from long-term pricing and preferential access to advanced models trained on their own data.
             </p>
             <ul className="space-y-4">
                {[
                  "Roadmap priority influence",
                  "Institutional memory embedding",
                  "Refined predictive model access",
                  "Fixed long-term pricing structures"
                ].map((li, i) => (
                  <li key={i} className="flex items-center space-x-3 text-white/60 font-bold">
                    <CheckCircle2 className="text-brand-blue" size={20} />
                    <span>{li}</span>
                  </li>
                ))}
             </ul>
          </div>
          <div className="p-12 glass-island rounded-[4rem] border-white/10 bg-brand-blue/[0.02] text-center">
             <Globe size={100} className="text-brand-blue mx-auto mb-8 animate-spin-slow" />
             <h4 className="text-2xl font-black uppercase tracking-tighter text-white">Global Scalability</h4>
             <p className="text-white/40 mt-4">Grow alongside the platform as it matures globally.</p>
          </div>
        </div>
      </section>

      {/* 10. OPEN ECOSYSTEM */}
      <section className="py-40 px-6 bg-brand-black border-y border-white/5">
        <div className="max-w-7xl mx-auto text-center">
           <h2 className="text-xs uppercase tracking-[0.5em] text-brand-blue font-black mb-12">OPEN BY DESIGN</h2>
           <h3 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter mb-12">Integrated by Default.</h3>
           <div className="flex flex-wrap justify-center gap-8">
              {["Mobility Platforms", "Safety Systems", "IoT Networks", "Environmental Sensors", "Partner Analytics"].map((item, i) => (
                <div key={i} className="px-10 py-6 glass-island rounded-2xl border-white/5 text-sm font-black uppercase tracking-widest text-white/40 hover:text-brand-blue hover:border-brand-blue/40 transition-all">
                  {item}
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* 11. SOCIETAL IMPACT */}
      <section className="py-40 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-24">
          <h2 className="text-3xl md:text-5xl font-black mb-12 tracking-tighter uppercase leading-none text-white">Societal Impact.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-12 text-left">
          <div className="p-16 glass-island rounded-[4rem] border-white/10 space-y-8 hover:border-brand-blue/30 transition-all group">
             <div className="flex items-center gap-6 mb-4">
                <Users size={48} className="text-brand-blue" />
                <h5 className="text-3xl font-serif italic text-white group-hover:text-brand-blue transition-colors">For Citizens</h5>
             </div>
             <p className="text-2xl text-white font-bold leading-relaxed opacity-60">
                Faster response times, greater safety, transparency, and real-time communication during both crises and everyday life.
             </p>
          </div>
          
          <div className="p-16 glass-island rounded-[4rem] border-white/10 space-y-8 hover:border-brand-blue/30 transition-all group">
             <div className="flex items-center gap-6 mb-4">
                <Target size={48} className="text-brand-blue" />
                <h5 className="text-3xl font-serif italic text-white group-hover:text-brand-blue transition-colors">For Leaders</h5>
             </div>
             <p className="text-2xl text-white font-bold leading-relaxed opacity-60">
                Stay visible and stay elected. Demonstrate operational excellence through predictive planning, effective resource allocation, and measurable outcomes.
             </p>
          </div>
        </div>
      </section>

      {/* 12. FINAL CTA - SEPARATED AND MOVED DOWN */}
      <section className="py-80 px-6 bg-brand-blue/[0.02] border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-brand-blue/5 blur-[120px] rounded-full -translate-y-1/2 pointer-events-none"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-[10px] uppercase tracking-[0.5em] text-brand-blue font-black mb-12">INITIALIZE DEPLOYMENT</h2>
          <h3 className="text-5xl md:text-[6rem] font-serif italic text-white mb-16 tracking-tighter leading-none">
            Start building your <br /> City Brain<span className="text-brand-blue not-italic animate-pulse">.</span>
          </h3>
          <p className="text-xl text-white/40 font-bold mb-20 uppercase tracking-widest">
            Every government, organization, and community is different.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            <a 
              href="mailto:mdahhan@boulai.net" 
              className="group inline-flex items-center space-x-6 px-16 py-8 bg-brand-blue text-white font-black rounded-2xl uppercase text-xs tracking-[0.3em] hover:scale-105 transition-all shadow-[0_0_50px_rgba(86,190,249,0.2)]"
            >
              <span>Request Vision Walkthrough</span>
              <ChevronRight size={20} className="group-hover:translate-x-2 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Internal icon for prediction used in loop
const BrainCircuit = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .52 8.105 4 4 0 0 0 5.327 3.53 4 4 0 0 0 8.35-3.53 4 4 0 0 0 .52-8.105 4 4 0 0 0-2.526-5.77A3 3 0 1 0 12 5z" />
    <path d="M9 13h4.5a1.5 1.5 0 0 1 0 3H9" />
    <path d="M11 9h2a1.5 1.5 0 0 1 0 3h-2a1.5 1.5 0 0 1 0-3z" />
  </svg>
);

const CheckCircle2 = ({ className, size }: { className?: string, size?: number }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export default SmartCityPage;